import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * usePortalDeepLink — Portal email deep-link handler (Epic 11 · S11.2, spec
 * Part E.10). Emails link `?portal=<parkId>&open=<what>`. This hook reads those
 * params ONCE on mount, clears them from the URL via `history.replaceState` (so
 * a refresh doesn't reopen), and hands them to the page so it can select that
 * park + open the right thing.
 *
 * `open` kinds (Part E.10):
 *   - `item:<id>`     → DrillOver → ItemDetail.
 *   - `message:<id>`  → conversation scrolled to that message (ConversationPanel
 *                      loads older up to 5× until found — Epic 7).
 *   - `done`          → scroll to Done recently.
 *   - none            → just open on that park.
 *   - `notifications` → account-wide notification settings — SEPARATE build;
 *                      this page does NOT handle it (returned as-is so a
 *                      higher layer can route, e.g. App.js).
 *
 * A `?portal=` for a park the login cannot open is surfaced as
 * "not authorised for this park" verbatim by the getPortal load error (the
 * backend raises it; PortalPage shows it in the ErrorState).
 *
 * Returns `{ deepParkId, open }` — both null when no deep link. `deepParkId` is
 * the raw `?portal=` value (a park UUID or null when absent). The hook clears
 * `portal` + `open` from the URL on mount; subsequent re-renders do not re-read
 * them (the link fires once, like Part E.10 mandates).
 */
const PARSE_OPEN = (raw) => {
  if (!raw) return null;
  // item:<id> / message:<id> / done / notifications
  if (raw.startsWith('item:')) return { kind: 'item', id: raw.slice(5) };
  if (raw.startsWith('message:')) return { kind: 'message', id: raw.slice(8) };
  if (raw === 'done') return { kind: 'done' };
  if (raw === 'notifications') return { kind: 'notifications' };
  return { kind: 'item', id: raw }; // tolerant: bare id → item
};

export const usePortalDeepLink = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once on mount — a deep link fires once, then clears itself.
  const [link] = useState(() => {
    const park = searchParams.get('portal');
    const openRaw = searchParams.get('open');
    return {
      deepParkId: park, // null when absent
      open: openRaw ? PARSE_OPEN(openRaw) : null,
    };
  });

  // Clear `portal` + `open` from the URL so a refresh doesn't reopen (Part
  // E.10). Done once on mount. Uses the router's setSearchParams (which under a
  // real BrowserRouter writes to window.location via history.replaceState); the
  // spec's replaceState semantics are preserved without touching window directly.
  useEffect(() => {
    if (link.deepParkId === null && link.open === null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('portal');
      next.delete('open');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return link;
};

export default usePortalDeepLink;
